/**
 * useReducedMotion (A11Y-4)
 *
 * Returns `true` when the user has asked the OS to reduce motion.
 * - Web: subscribes to the `prefers-reduced-motion` media query.
 * - Native: reads `AccessibilityInfo.isReduceMotionEnabled()` and listens for
 *   `reduceMotionChanged`.
 *
 * Components use this to skip or shorten non-essential animations. Defaults to
 * `false` (full animation) when the preference can't be read.
 */

import { useEffect, useState } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';
import {
  REDUCED_MOTION_MEDIA_QUERY,
  getWebReducedMotionInitial,
} from '../helpers/reducedMotion.helper';

export const useReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState<boolean>(() =>
    Platform.OS === 'web' ? getWebReducedMotionInitial() : false
  );

  useEffect(() => {
    let mounted = true;

    if (Platform.OS === 'web') {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return;
      }
      const mql = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
      const onChange = (event: MediaQueryListEvent) => {
        if (mounted) setReduced(event.matches);
      };
      mql.addEventListener?.('change', onChange);
      setReduced(mql.matches);
      return () => {
        mounted = false;
        mql.removeEventListener?.('change', onChange);
      };
    }

    AccessibilityInfo.isReduceMotionEnabled?.()
      .then((value) => {
        if (mounted) setReduced(Boolean(value));
      })
      .catch(() => {
        // Keep default (false) when the query is unavailable.
      });

    const subscription = AccessibilityInfo.addEventListener?.(
      'reduceMotionChanged',
      (value: boolean) => {
        if (mounted) setReduced(Boolean(value));
      }
    );

    return () => {
      mounted = false;
      subscription?.remove?.();
    };
  }, []);

  return reduced;
};
